import { baseValue, poseOf, type ChannelValues } from '../animation/timeline';
import { POSE_KEYS, channelRange, type AvMapping, type AvSession, type Channel, type PoseKey, type SceneObject } from '../domain/schemas';
import { envelopeAt, envelopeLength, shapeOf } from './envelope';
import { lowerBound, type ExtractedEvents, type MusicEvent } from './events';
import { TICKS_PER_BEAT, TICKS_PER_STEP, quantizeTicks, ticksToSeconds } from './time';

/**
 * Audio-reactive overlays (Phase 4A). Everything here is a pure function of the session,
 * the extracted events and a time in seconds: no state is carried from frame to frame, so
 * seeking to T gives exactly what continuous playback shows at T.
 *
 * Triggers: a mapping's matching events, filtered by section and velocity, thinned to every
 * Nth (from `phase`), snapped to a beat subdivision, shifted by `offsetBeats`, then thinned
 * by the cooldown. They are computed once per (mapping, extraction) and cached.
 *
 * Level of a mapping at T: the envelope of every trigger at or before T whose envelope
 * still covers T (at most MAX_ACTIVE_ENVELOPES, the most recent), each scaled by velocity;
 * overlapping envelopes of one mapping combine by their MAXIMUM, so a dense hi-hat run
 * cannot stack up to an unbounded pulse.
 *
 * Composition at T, in this fixed order (see `composeObject`):
 *   1. base scene geometry → 2. Phase 3B animation values at frame(T) → 3. reactions and
 *   motion-cue overlays (cues.ts): position and rotation ADD; scale MULTIPLIES; opacity ADDS then clamps to [0, 1];
 *   pose offsets ADD then clamp to pose limits; tint: the strongest wins (ties: the first
 *   mapping); visibility: toggles flip by trigger parity, then an active "hide" beats an
 *   active "show". Different mappings combine in session order; every operation is
 *   commutative except visibility precedence, which is fixed above.
 */
export const MAX_ACTIVE_ENVELOPES = 8;
/** Shake changes direction at this rate (deterministic, seeded). */
export const SHAKE_HZ = 30;

export interface Trigger {
  tick: number;
  seconds: number;
  /** 0–1 (1 for events without velocity). */
  velocity: number;
  eventId: string;
}

const cache = new WeakMap<ExtractedEvents, WeakMap<AvMapping, Trigger[]>>();

export function mappingTriggers(mapping: AvMapping, extracted: ExtractedEvents): Trigger[] {
  let perExtraction = cache.get(extracted);
  if (!perExtraction) cache.set(extracted, (perExtraction = new WeakMap()));
  const hit = perExtraction.get(mapping);
  if (hit) return hit;
  const lists = mapping.source.categories.map((c) => extracted.byCategory.get(c) ?? []);
  const merged: MusicEvent[] = lists.length === 1 ? lists[0]! : lists.flat().sort((a, b) => a.tick - b.tick || a.id.localeCompare(b.id));
  const { every, phase, minVelocity, sectionId } = mapping.source;
  const offset = Math.round(mapping.offsetBeats * TICKS_PER_BEAT);
  const cooldown = Math.round(mapping.cooldownBeats * TICKS_PER_BEAT);
  const out: Trigger[] = [];
  let k = 0;
  let last = -Infinity;
  for (const e of merged) {
    if (sectionId !== null && !(e.source.kind === 'note' && e.source.sectionId === sectionId) && !(e.source.kind === 'section' && e.source.sectionId === sectionId)) {
      if (e.source.kind === 'note' || e.source.kind === 'section') continue;
      // Grid, clip and marker events: inside the section's span.
      const s = extracted.sections.find((x) => x.id === sectionId);
      if (!s || e.tick < s.startStep * TICKS_PER_STEP || e.tick >= s.endStep * TICKS_PER_STEP) continue;
    }
    if (e.velocity < minVelocity) continue;
    const index = k++;
    if (index < phase || (index - phase) % every !== 0) continue;
    let tick = mapping.quantize ? quantizeTicks(e.tick, mapping.quantize) : e.tick;
    tick += offset;
    if (tick < 0) continue;
    if (tick - last < cooldown) continue;
    // Two events landing on one tick (e.g. kick + 808, or after snapping) are one trigger.
    const prev = out[out.length - 1];
    if (prev && prev.tick === tick) {
      if (e.velocity > prev.velocity) out[out.length - 1] = { ...prev, velocity: e.velocity };
      continue;
    }
    out.push({ tick, seconds: ticksToSeconds(tick, extracted.bpm), velocity: e.velocity, eventId: e.id });
    last = tick;
  }
  // Snapping and offsets can reorder neighbours slightly; keep triggers sorted.
  out.sort((a, b) => a.tick - b.tick);
  perExtraction.set(mapping, out);
  return out;
}

/** Index of the last trigger at or before `seconds` (−1 when none). */
function lastAtOrBefore(triggers: readonly Trigger[], seconds: number): number {
  let lo = 0;
  let hi = triggers.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (triggers[mid]!.seconds <= seconds) lo = mid + 1;
    else hi = mid;
  }
  return lo - 1;
}

export interface MappingLevel {
  level: number;
  /** Envelopes contributing at this instant. */
  active: number;
  /** Index of the trigger giving the maximum (for seeded effects), or −1. */
  trigger: number;
  /** Seconds since that trigger. */
  since: number;
  /** Triggers at or before T (visibility toggles). */
  count: number;
}

/** A mapping's level at `seconds` (pure). */
export function mappingLevel(mapping: AvMapping, triggers: readonly Trigger[], seconds: number): MappingLevel {
  const shaped = shapeOf(mapping.envelope);
  const length = shaped.attack + shaped.hold + shaped.decay;
  const lastIndex = lastAtOrBefore(triggers, seconds);
  let level = 0;
  let active = 0;
  let best = -1;
  let since = 0;
  for (let i = lastIndex; i >= 0 && active < MAX_ACTIVE_ENVELOPES; i--) {
    const t = seconds - triggers[i]!.seconds;
    if (t >= length) break;
    const v = envelopeAt(shaped, t) * (1 - mapping.velocitySensitivity + mapping.velocitySensitivity * triggers[i]!.velocity);
    if (v > 0) active++;
    if (v > level) {
      level = v;
      best = i;
      since = t;
    }
  }
  return { level, active, trigger: best, since, count: lastIndex + 1 };
}

/** Additive and multiplicative reaction of one object at one instant. */
export interface ObjectReaction {
  dx: number;
  dy: number;
  rotation: number;
  /** Uniform scale factor (reactions) and per-axis factors (motion cues). */
  scale: number;
  sx: number;
  sy: number;
  opacity: number;
  pose: Partial<Record<PoseKey, number>>;
  tint: { color: string; strength: number } | null;
  /** Visibility toggles so far (parity flips), and active show/hide envelopes. */
  toggles: number;
  show: boolean;
  hide: boolean;
}

export interface SceneEffects {
  flash: { color: string; alpha: number } | null;
  background: { color: string; alpha: number } | null;
  shake: { x: number; y: number };
}

export interface ReactionFrame {
  objects: Map<string, ObjectReaction>;
  effects: SceneEffects;
  /** Active envelopes across all mappings (diagnostics). */
  active: number;
}

export const POSE_PRESETS: Record<NonNullable<AvMapping['params']['pose']>, Partial<Record<PoseKey, number>>> = {
  'arms-up': { armL: 150, armR: -150 },
  crouch: { torsoY: 0.05, legL: 15, legR: -15 },
  lean: { torso: 12 },
  'head-bob': { head: 12, headY: 0.02 },
  'kick-out': { legR: -45, armL: 20 },
};

export const emptyReaction = (): ObjectReaction => ({ dx: 0, dy: 0, rotation: 0, scale: 1, sx: 1, sy: 1, opacity: 0, pose: {}, tint: null, toggles: 0, show: false, hide: false });

/** Deterministic pseudo-random in [−1, 1] from integers (no Math.random, no state). */
function hashUnit(a: number, b: number, c: number): number {
  let h = (Math.imul(a ^ 0x9e3779b9, 2654435761) ^ Math.imul(b + 0x7f4a7c15, 2246822519) ^ Math.imul(c + 0x165667b1, 3266489917)) >>> 0;
  h = Math.imul(h ^ (h >>> 15), 2246822519) >>> 0;
  h ^= h >>> 13;
  return ((h >>> 0) / 4294967295) * 2 - 1;
}

/** Every mapping of a session evaluated at `seconds` (pure, cached triggers). */
export function evaluateReactions(session: Pick<AvSession, 'mappings'>, extracted: ExtractedEvents, seconds: number): ReactionFrame {
  const objects = new Map<string, ObjectReaction>();
  const effects: SceneEffects = { flash: null, background: null, shake: { x: 0, y: 0 } };
  let active = 0;
  for (const m of session.mappings) {
    if (!m.enabled) continue;
    const triggers = mappingTriggers(m, extracted);
    if (triggers.length === 0) continue;
    const l = mappingLevel(m, triggers, seconds);
    active += l.active;
    const amount = m.params.amount;
    if (m.target.kind === 'scene') {
      if (l.level <= 0) continue;
      const strength = Math.min(1, Math.max(0, amount)) * l.level;
      if (m.reaction === 'flash' && strength > (effects.flash?.alpha ?? 0)) effects.flash = { color: m.params.color ?? '#ffffff', alpha: strength };
      else if (m.reaction === 'background' && strength > (effects.background?.alpha ?? 0)) effects.background = { color: m.params.color ?? '#ffffff', alpha: strength };
      else if (m.reaction === 'shake') {
        const amp = Math.min(100, Math.max(0, amount)) * l.level;
        const step = Math.floor(l.since * SHAKE_HZ);
        effects.shake.x += amp * hashUnit(m.seed, l.trigger, step * 2);
        effects.shake.y += amp * hashUnit(m.seed, l.trigger, step * 2 + 1);
      }
      continue;
    }
    const id = m.target.objectId;
    let r = objects.get(id);
    if (m.reaction === 'visibility') {
      if (!r) objects.set(id, (r = emptyReaction()));
      if (m.params.mode === 'toggle') r.toggles += l.count;
      else if (l.level > 0) {
        if (m.params.mode === 'hide') r.hide = true;
        else r.show = true;
      }
      continue;
    }
    if (l.level <= 0) continue;
    if (!r) objects.set(id, (r = emptyReaction()));
    const v = amount * l.level;
    switch (m.reaction) {
      case 'opacity':
        r.opacity += v;
        break;
      case 'scale':
        r.scale *= Math.max(0, 1 + v);
        break;
      case 'position': {
        const d = m.params.direction ?? 'up';
        if (d === 'up') r.dy -= v;
        else if (d === 'down') r.dy += v;
        else if (d === 'left') r.dx -= v;
        else r.dx += v;
        break;
      }
      case 'rotation':
        r.rotation += v;
        break;
      case 'tint': {
        const strength = Math.min(1, Math.max(0, v));
        if (strength > (r.tint?.strength ?? 0)) r.tint = { color: m.params.color ?? '#ffffff', strength };
        break;
      }
      case 'pose':
        for (const [k, off] of Object.entries(POSE_PRESETS[m.params.pose!]) as [PoseKey, number][]) r.pose[k] = (r.pose[k] ?? 0) + off * v;
        break;
    }
  }
  return { objects, effects, active };
}

const clampChannel = (channel: Channel, v: number) => {
  const [lo, hi] = channelRange(channel);
  return Math.min(hi, Math.max(lo, v));
};

/**
 * The displayed channel values of one object: base → animation → reactions (see the
 * module comment for the rules). Returns `animated` unchanged when there is no reaction.
 */
export function composeObject(base: SceneObject, animated: ChannelValues | undefined, r: ObjectReaction | undefined): ChannelValues | undefined {
  if (!r) return animated;
  const a = animated ?? {};
  const out: ChannelValues = { ...a };
  const get = (c: Channel) => a[c] ?? baseValue(base, c);
  if (r.dx) out.x = clampChannel('x', get('x') + r.dx);
  if (r.dy) out.y = clampChannel('y', get('y') + r.dy);
  if (r.rotation) out.rotation = clampChannel('rotation', get('rotation') + r.rotation);
  if (r.scale !== 1 || r.sx !== 1) out.scaleX = clampChannel('scaleX', (a.scaleX ?? 1) * r.scale * r.sx);
  if (r.scale !== 1 || r.sy !== 1) out.scaleY = clampChannel('scaleY', (a.scaleY ?? 1) * r.scale * r.sy);
  if (r.opacity) out.opacity = clampChannel('opacity', get('opacity') + r.opacity);
  if (base.type === 'character') {
    const pose = poseOf(base);
    for (const k of POSE_KEYS) {
      const off = r.pose[k];
      if (!off) continue;
      const c = `pose.${k}` as Channel;
      out[c] = clampChannel(c, (a[c] ?? pose[k]) + off);
    }
  }
  if (r.toggles % 2 === 1 || r.show || r.hide) {
    let visible = (a.visible ?? (base.visible ? 1 : 0)) >= 0.5;
    if (r.toggles % 2 === 1) visible = !visible;
    if (r.hide) visible = false;
    else if (r.show) visible = true;
    out.visible = visible ? 1 : 0;
  }
  return out;
}

/** Envelope length of a mapping in seconds (for the timeline). */
export const mappingLength = (m: AvMapping): number => envelopeLength(m.envelope);

/** Triggers of a mapping between two times (timeline drawing). */
export function triggersBetween(triggers: readonly Trigger[], from: number, to: number): Trigger[] {
  const i = lowerBound(triggers.map((t) => ({ tick: t.seconds })), from);
  const out: Trigger[] = [];
  for (let j = i; j < triggers.length && triggers[j]!.seconds <= to; j++) out.push(triggers[j]!);
  return out;
}
